import { useLayoutEffect } from "react"
import styled from "@emotion/styled"
import { motion, useMotionValue } from "framer-motion"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useEnv } from "contexts/Env"
import { useUser } from "contexts/User"

import getMediaQuery from "css/breakpoints"

import { getPriceDatas } from "components/price/utils"
import { waTriggerEvent } from "../analytics/DigitalDataLayer"

const Root = styled.ul`
  position: absolute;
  inset: auto 0 -1rem 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1ex;
  /* outline: 1px solid magenta; */

  ${getMediaQuery("m")} {
    inset: auto 0 0 0;
  }
  html.prefers-contrast & {
    background: rgb(var(--pure-white));
  }

  > li {
    all: unset;
    width: 1.75rem;
    height: 1.75rem;
  }
`

const Button = styled(motion.button)`
  all: unset;
  touch-action: none;
  width: 100%;
  height: 100%;
  pointer-events: var(--events);
  cursor: var(--cursor);
  opacity: var(--op);
  & > svg {
    width: 100%;
    height: auto;
    --pi: 3.14159265359;
    --r: 45px;
    --strk: calc(var(--pi) * 2 * var(--r));
  }

  & > svg circle {
    transition: stroke-dashoffset 1s linear;
    stroke: rgb(var(--fill) / 0.3);
    stroke-width: var(--nostroke);
    r: var(--r);
    fill: transparent;
    stroke-dasharray: 0;
    stroke-dashoffset: 0;
    transition: fill 300ms, stroke 300ms;
  }

  & > svg circle:nth-of-type(2) {
    stroke: rgb(var(--fill) / calc(0.6 + 0.4 * var(--t)));
    stroke-dasharray: var(--strk);
    stroke-dashoffset: calc(var(--strk) * (1 - var(--p)));
  }

  & > svg circle:nth-of-type(3) {
    stroke-dasharray: unset;
    stroke-dashoffset: unset;
    stroke-width: unset;
    stroke: unset;
    fill: rgb(var(--fill) / calc(0.6 + 0.4 * var(--t)));
    r: calc(var(--r) - 11px);
  }
`

export default function Switch({ children, height }) {
  const console = useConsole()

  console.verbose("FamilyVariations:Variations:Ballerina:Switch(%s) {%o}", {})
  return <Root>{children}</Root>
}

export function Switcher({ active, variation, disabled, setVariation, theme }) {
  const console = useConsole()
  const progress = useMotionValue(variation.percent)
  const dictionary = useDictionary()
  const locale = useLocale()
  const env = useEnv()
  const { countryCode } = useUser()

  useLayoutEffect(() => {
    variation.onprogress = p => {
      console.verbose("FamilyVariations:Switcher(%s):progress(%s)", variation.rmc, p)
      progress.set(p)
    }

    progress.set(variation.percent)
    return () => (variation.onprogress = null)
  })
  console.verbose("FamilyVariations:Switcher(%s) {%o}", variation.rmc, { variation })

  async function onClick() {
    setVariation(variation.rmc)

    const { price, currency } = await getPriceDatas({ rmc: variation.rmc, countryCode, locale, env })

    waTriggerEvent({
      eventName: "familyVariations",
      eventType: "Click",
      products: [
        {
          productCategory: variation?.family,
          productModel: variation?.titleCode,
          productID: variation.rmc,
          productQuantity: "1",
          productPrice: price,
          currency: currency,
        },
      ],
    })
  }

  return (
    <li>
      <Button
        aria-label={!active ? dictionary.viewOtherVariation() : dictionary.variationSelected()}
        onClick={onClick}
        style={{
          "--p": progress,
          "--t": +active,
          "--fill": theme?.theme === "dark-theme" ? "255 255 255" : "33 33 33",
          "--events": disabled ? "none" : "auto",
          "--cursor": active ? "default" : "pointer",
          "--op": !active ? 0.3 : 1,
          "--nostroke": +active ? 6 : 0,
        }}
      >
        <svg width='100' height='100' viewBox='0 0 100 100' aria-hidden='true'>
          <circle cx='50' cy='50' />
          {<circle cx='50' cy='50' />}
          <circle cx='50' cy='50' />
        </svg>
      </Button>
    </li>
  )
}
